import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType } from 'docx';

const {FiTarget, FiCopy, FiDownload, FiMail, FiRefreshCw, FiExternalLink, FiCheckCircle, FiFileText, FiFile} = FiIcons;

const ResultsPage = ({ formData, onRestart }) => {
  const [copied, setCopied] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  const generatePlan = () => {
    const channelsText = formData.chosenChannels?.join(', ') || 'your chosen platforms';
    
    return `🎯 Your GospelWise Mini Marketing Plan

You're writing a book for ${formData.bookReader}, centered around the message: "${formData.bookMessage}". Your goal is to help readers experience: ${formData.bookTransformation}.

Your launch goal is ${formData.launchGoal}, with success measured by ${formData.launchMetrics} within ${formData.timeline}. This clear target will guide every marketing decision you make.

To connect with your audience, you'll lean into your strengths as ${formData.authorStrengths}, and create content that feels natural—like ${formData.contentStyle}. You've chosen to focus on ${channelsText} as your main marketing methods.

For outreach, you'll explore ads on ${formData.chosenAdPlatform}, keeping it simple and aligned with where your readers already spend time.

🎬 Your first step? ${formData.firstStep}.

🙌 Well done! You've built a faithful, manageable start to marketing your book with clear goals and actionable strategies.`;
  };

  const marketingPlan = generatePlan();

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(marketingPlan);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const downloadText = () => {
    const element = document.createElement('a');
    const file = new Blob([marketingPlan], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = 'my-gospelwise-marketing-plan.txt';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const downloadPDF = async () => {
    setIsGenerating(true);
    try {
      const pdf = new jsPDF();
      
      // Set font sizes and colors
      pdf.setFontSize(24);
      pdf.setTextColor(37, 99, 235); // Blue color
      
      // Title
      pdf.text('🎯 Your GospelWise Mini Marketing Plan', 20, 30);
      
      pdf.setFontSize(12);
      pdf.setTextColor(0, 0, 0);
      
      // Content
      const lines = marketingPlan.split('\n');
      let yPosition = 50;
      
      lines.forEach((line, index) => {
        if (line.trim()) {
          if (line.includes('🎯')) {
            pdf.setFontSize(16);
            pdf.setTextColor(37, 99, 235);
          } else if (line.includes('🎬')) {
            pdf.setFontSize(14);
            pdf.setTextColor(234, 88, 12); // Orange color
          } else if (line.includes('🙌')) {
            pdf.setFontSize(14);
            pdf.setTextColor(22, 163, 74); // Green color
          } else {
            pdf.setFontSize(12);
            pdf.setTextColor(0, 0, 0);
          }
          
          // Handle long lines by wrapping them
          const splitLines = pdf.splitTextToSize(line, 170);
          splitLines.forEach((splitLine) => {
            if (yPosition > 270) {
              pdf.addPage();
              yPosition = 20;
            }
            pdf.text(splitLine, 20, yPosition);
            yPosition += 7;
          });
        }
      });
      
      // Add footer
      pdf.setFontSize(10);
      pdf.setTextColor(128, 128, 128);
      pdf.text('Generated by GospelWise Author™ Marketing Planner', 20, 280);
      pdf.text(`Date: ${new Date().toLocaleDateString()}`, 20, 285);
      
      pdf.save('my-gospelwise-marketing-plan.pdf');
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  const downloadWord = async () => {
    setIsGenerating(true);
    try {
      const doc = new Document({
        sections: [{
          properties: {},
          children: [
            new Paragraph({
              children: [
                new TextRun({
                  text: "🎯 Your GospelWise Mini Marketing Plan",
                  bold: true,
                  size: 32,
                  color: "2563EB"
                })
              ],
              heading: HeadingLevel.TITLE,
              alignment: AlignmentType.CENTER,
              spacing: { after: 400 }
            }),
            ...marketingPlan.split('\n').map(line => 
              new Paragraph({
                children: [
                  new TextRun({
                    text: line,
                    size: 24,
                    color: line.includes('🎯') ? "2563EB" : 
                           line.includes('🎬') ? "EA580C" :
                           line.includes('🙌') ? "16A34A" : "000000"
                  })
                ],
                spacing: { after: 200 }
              })
            ),
            new Paragraph({
              children: [
                new TextRun({
                  text: "Generated by GospelWise Author™ Marketing Planner",
                  size: 20,
                  color: "808080",
                  italics: true
                })
              ],
              alignment: AlignmentType.CENTER,
              spacing: { before: 600 }
            }),
            new Paragraph({
              children: [
                new TextRun({
                  text: `Date: ${new Date().toLocaleDateString()}`,
                  size: 20,
                  color: "808080",
                  italics: true
                })
              ],
              alignment: AlignmentType.CENTER
            })
          ]
        }]
      });

      const buffer = await Packer.toBuffer(doc);
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
      const url = URL.createObjectURL(blob);
      
      const element = document.createElement('a');
      element.href = url;
      element.download = 'my-gospelwise-marketing-plan.docx';
      document.body.appendChild(element);
      element.click();
      document.body.removeChild(element);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating Word document:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  const emailPlan = () => {
    const subject = encodeURIComponent('My GospelWise Marketing Plan');
    const body = encodeURIComponent(marketingPlan);
    window.open(`mailto:?subject=${subject}&body=${body}`);
  };

  return (
    <div className="max-w-4xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="text-center mb-8"
      >
        <div className="bg-gradient-to-r from-green-600 to-blue-600 p-4 rounded-full w-20 h-20 mx-auto mb-6 flex items-center justify-center">
          <SafeIcon icon={FiCheckCircle} className="text-3xl text-white" />
        </div>
        <h1 className="text-4xl font-bold text-gray-800 mb-4">
          Congratulations! 🎉
        </h1>
        <p className="text-xl text-gray-600">
          Your personalized marketing plan is ready!
        </p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.3 }}
        className="bg-white rounded-2xl shadow-xl p-8 mb-8"
      >
        <div className="bg-gradient-to-r from-blue-50 to-orange-50 p-6 rounded-xl mb-6">
          <pre className="whitespace-pre-wrap text-gray-800 leading-relaxed font-sans">
            {marketingPlan}
          </pre>
        </div>

        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-700 mb-3 text-center">
            📥 Download Your Plan
          </h3>
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={downloadPDF}
              disabled={isGenerating}
              className="bg-red-600 text-white px-4 py-3 rounded-xl font-semibold flex items-center justify-center hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <SafeIcon icon={FiFileText} className="mr-2" />
              {isGenerating ? 'Generating...' : 'Download PDF'}
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={downloadWord}
              disabled={isGenerating}
              className="bg-blue-700 text-white px-4 py-3 rounded-xl font-semibold flex items-center justify-center hover:bg-blue-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <SafeIcon icon={FiFile} className="mr-2" />
              {isGenerating ? 'Generating...' : 'Download Word'}
            </motion.button>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={copyToClipboard}
            className="bg-green-600 text-white px-4 py-3 rounded-xl font-semibold flex items-center justify-center hover:bg-green-700 transition-colors"
          >
            <SafeIcon icon={copied ? FiCheckCircle : FiCopy} className="mr-2" />
            {copied ? 'Copied!' : 'Copy Plan'}
          </motion.button>

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={downloadText}
            className="bg-gray-600 text-white px-4 py-3 rounded-xl font-semibold flex items-center justify-center hover:bg-gray-700 transition-colors"
          >
            <SafeIcon icon={FiDownload} className="mr-2" />
            Download Text
          </motion.button>

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={emailPlan}
            className="bg-orange-600 text-white px-4 py-3 rounded-xl font-semibold flex items-center justify-center hover:bg-orange-700 transition-colors"
          >
            <SafeIcon icon={FiMail} className="mr-2" />
            Email Plan
          </motion.button>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.5 }}
        className="bg-gradient-to-r from-orange-500 to-red-500 rounded-2xl shadow-xl p-8 text-white text-center mb-8"
      >
        <h2 className="text-2xl font-bold mb-4">Ready for the Complete Strategy?</h2>
        <p className="text-lg mb-6 opacity-90">
          Turn your mini-plan into a complete launch + legacy strategy with the full 
          GospelWise Author™ Marketing Playbook
        </p>
        
        <motion.a
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          href="https://www.crossandpen.com/resources/marketing-playbook/"
          target="_blank"
          rel="noopener noreferrer"
          className="bg-white text-orange-600 px-8 py-4 rounded-xl font-bold text-lg inline-flex items-center hover:bg-gray-100 transition-colors shadow-lg"
        >
          Get the Full Playbook
          <SafeIcon icon={FiExternalLink} className="ml-2" />
        </motion.a>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.7 }}
        className="text-center"
      >
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={onRestart}
          className="bg-gray-600 text-white px-6 py-3 rounded-xl font-semibold flex items-center mx-auto hover:bg-gray-700 transition-colors"
        >
          <SafeIcon icon={FiRefreshCw} className="mr-2" />
          Start Over
        </motion.button>
      </motion.div>
    </div>
  );
};

export default ResultsPage;